import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "@hooks/dark-mode";
import KnowledgeBankLayout from "./KnowledgeBankLayout";
import {
  ChevronLeft,
  ChevronRight,
  Book,
  Leaf,
  FileText,
  Clock,
  Car,
  Package,
  HelpCircle
} from "lucide-react";

// Category icons mapping
const categoryIcons: Record<string, React.ElementType> = {
  gettingStarted: Book,
  sustainability: Leaf,
  compliance: FileText,
  timeRegistration: Clock,
  drivingAllowance: Car,
  producerResponsibility: Package,
  faq: HelpCircle
};

// Navigation structure
const navigationStructure: Record<string, string[]> = {
  gettingStarted: ["introduction", "quickStart", "accountSetup"],
  sustainability: ["co2Tracking", "esgReporting", "emissionFactors"],
  compliance: ["csrd", "euTaxonomy", "ghgProtocol"],
  timeRegistration: ["setup", "mobileApp", "reports"],
  drivingAllowance: ["gpsTracking", "taxRules", "approval"],
  producerResponsibility: ["eprOverview", "packagingData", "feeCalculation"],
  faq: ["general", "technical", "billing"]
};

const categoryOrder = [
  "gettingStarted",
  "sustainability",
  "compliance",
  "timeRegistration",
  "drivingAllowance",
  "producerResponsibility",
  "faq"
];

const Article = () => {
  const { t } = useTranslation();
  const { dark } = useDarkMode();
  const { categoryId, articleId } = useParams<{ categoryId: string; articleId: string }>();
  const navigate = useNavigate();

  const titleColor = dark ? "#ffffff" : "#111827";
  const textColor = dark ? "#d1d5db" : "#4b5563";
  const accent = dark ? "#60a5fa" : "#3b82f6";
  const borderColor = dark ? "#374151" : "#e5e7eb";
  const codeBg = dark ? "#1f2937" : "#f3f4f6";

  // Get navigation info
  const getNavigation = () => {
    if (!categoryId || !articleId) return { prev: null, next: null };

    const categoryIndex = categoryOrder.indexOf(categoryId);
    const articles = navigationStructure[categoryId] || [];
    const articleIndex = articles.indexOf(articleId);

    let prev = null;
    let next = null;

    // Previous article
    if (articleIndex > 0) {
      prev = {
        category: categoryId,
        article: articles[articleIndex - 1]
      };
    } else if (categoryIndex > 0) {
      const prevCategory = categoryOrder[categoryIndex - 1];
      const prevArticles = navigationStructure[prevCategory];
      prev = {
        category: prevCategory,
        article: prevArticles[prevArticles.length - 1]
      };
    }

    // Next article
    if (articleIndex < articles.length - 1) {
      next = {
        category: categoryId,
        article: articles[articleIndex + 1]
      };
    } else if (categoryIndex < categoryOrder.length - 1) {
      const nextCategory = categoryOrder[categoryIndex + 1];
      const nextArticles = navigationStructure[nextCategory];
      next = {
        category: nextCategory,
        article: nextArticles[0]
      };
    }

    return { prev, next };
  };

  const { prev, next } = getNavigation();
  const Icon = categoryIcons[categoryId || ""] || Book;

  // Redirect if invalid route
  React.useEffect(() => {
    if (categoryId && articleId) {
      const articles = navigationStructure[categoryId];
      if (!articles || !articles.includes(articleId)) {
        navigate("/knowledge-bank");
      }
    }
  }, [categoryId, articleId, navigate]);

  if (!categoryId || !articleId) {
    return null;
  }

  return (
    <KnowledgeBankLayout
      title={`${t(`knowledgeBank.categories.${categoryId}.articles.${articleId}`)} | ${t("knowledgeBank.title")}`}
    >
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-8">
          <Link to="/knowledge-bank" className="hover:underline" style={{ color: textColor }}>
            {t("knowledgeBank.title")}
          </Link>
          <ChevronRight className="w-4 h-4" style={{ color: textColor }} />
          <Link
            to={`/knowledge-bank/${categoryId}/${navigationStructure[categoryId]?.[0]}`}
            className="hover:underline"
            style={{ color: textColor }}
          >
            {t(`knowledgeBank.categories.${categoryId}.title`)}
          </Link>
          <ChevronRight className="w-4 h-4" style={{ color: textColor }} />
          <span style={{ color: accent }}>
            {t(`knowledgeBank.categories.${categoryId}.articles.${articleId}`)}
          </span>
        </nav>

        {/* Article Header */}
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${accent}15` }}
            >
              <Icon className="w-4 h-4" style={{ color: accent }} />
            </div>
            <span className="text-sm font-medium" style={{ color: accent }}>
              {t(`knowledgeBank.categories.${categoryId}.title`)}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: titleColor }}>
            {t(`knowledgeBank.categories.${categoryId}.articles.${articleId}`)}
          </h1>
          <p className="text-lg" style={{ color: textColor }}>
            {t(`knowledgeBank.categories.${categoryId}.descriptions.${articleId}`)}
          </p>
        </header>

        {/* Article Content */}
        <article
          className="prose prose-lg max-w-none"
          style={{
            color: textColor,
            ["--tw-prose-headings" as string]: titleColor,
            ["--tw-prose-body" as string]: textColor,
            ["--tw-prose-links" as string]: accent
          }}
        >
          {/* Placeholder content - you can replace this with actual article content */}
          <div
            className="p-6 rounded-xl mb-8"
            style={{ backgroundColor: codeBg }}
          >
            <p className="text-sm mb-0" style={{ color: textColor }}>
              {t("knowledgeBank.article.placeholder")}
            </p>
          </div>

          <h2 style={{ color: titleColor }}>{t("knowledgeBank.article.overviewTitle")}</h2>
          <p>
            {t(`knowledgeBank.categories.${categoryId}.content.${articleId}.overview`, {
              defaultValue: t("knowledgeBank.article.comingSoon")
            })}
          </p>

          <h2 style={{ color: titleColor }}>{t("knowledgeBank.article.keyPointsTitle")}</h2>
          <ul>
            <li>{t(`knowledgeBank.categories.${categoryId}.content.${articleId}.point1`, { defaultValue: t("knowledgeBank.article.comingSoon") })}</li>
            <li>{t(`knowledgeBank.categories.${categoryId}.content.${articleId}.point2`, { defaultValue: t("knowledgeBank.article.comingSoon") })}</li>
            <li>{t(`knowledgeBank.categories.${categoryId}.content.${articleId}.point3`, { defaultValue: t("knowledgeBank.article.comingSoon") })}</li>
          </ul>
        </article>

        {/* Navigation */}
        <nav
          className="flex items-center justify-between mt-12 pt-8 border-t"
          style={{ borderColor }}
        >
          {prev ? (
            <Link
              to={`/knowledge-bank/${prev.category}/${prev.article}`}
              className="group flex items-center gap-3 p-4 rounded-xl border transition-all hover:shadow-md max-w-[45%]"
              style={{ borderColor }}
            >
              <ChevronLeft className="w-5 h-5 flex-shrink-0" style={{ color: textColor }} />
              <div className="text-left min-w-0">
                <p className="text-xs mb-1" style={{ color: textColor }}>
                  {t("knowledgeBank.navigation.previous")}
                </p>
                <p className="font-medium truncate group-hover:text-blue-500 transition-colors" style={{ color: titleColor }}>
                  {t(`knowledgeBank.categories.${prev.category}.articles.${prev.article}`)}
                </p>
              </div>
            </Link>
          ) : (
            <div />
          )}

          {next ? (
            <Link
              to={`/knowledge-bank/${next.category}/${next.article}`}
              className="group flex items-center gap-3 p-4 rounded-xl border transition-all hover:shadow-md max-w-[45%]"
              style={{ borderColor }}
            >
              <div className="text-right min-w-0">
                <p className="text-xs mb-1" style={{ color: textColor }}>
                  {t("knowledgeBank.navigation.next")}
                </p>
                <p className="font-medium truncate group-hover:text-blue-500 transition-colors" style={{ color: titleColor }}>
                  {t(`knowledgeBank.categories.${next.category}.articles.${next.article}`)}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: textColor }} />
            </Link>
          ) : (
            <div />
          )}
        </nav>
      </div>
    </KnowledgeBankLayout>
  );
};

export default Article;
